/**
 * ValueEditor for the QueryBuilder.
 *
 * Single component that dispatches on `field.fieldType` (mirroring our
 * `FieldDefinitionDto.field_type`) and renders the appropriate CMS Core component
 * (per DR §5.12: "RQB renders our core components via thin adapters, not vanilla MUI").
 *
 * Component → Core mapping:
 *   - select / us_state_select / yes_no_na_unknown      → SingleSelectField
 *   - multiselect (and multi-value `in`/`notIn`)        → MultiSelectField
 *   - boolean                                           → Atomic Checkbox
 *   - date / datetime / time                            → DatePickerField, behind a target picker
 *                                                         (HierarchicalFieldSelector) on date / datetime
 *   - monetary                                          → MonetaryValueTextField
 *   - number / multi-value number                       → TextField type=number / MultiSelectField
 *   - string / multiline_string / location / contact    → TextField (multiline for *_string)
 *   - hierarchical                                      → MultiSelectField (parent) + MultiSelectField (children, disabled when multi-parent)
 *   - user                                              → the target picker (HierarchicalFieldSelector) over the other user fields
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { ValueEditorProps } from 'react-querybuilder';
import moment from 'moment';

import MenuItem from '~/components/core/Atomic/MenuItem';
import DatePickerField from '~/components/core/Molecules/Fields/DatePickerField';
import { MonetaryValueTextField } from '~/components/core/Molecules/Fields/MonetaryValueTextField';
import MultiSelectAutoComplete from '~/components/core/Molecules/Fields/MultiSelectAutoComplete';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { DateFormat } from '~/DateTimeUtils';
import COUNTRY_TO_STATE_MAP from '~/server_shared/generated-types/COUNTRY_TO_STATE_MAP';

import type { FieldOption } from './buildFieldTree';
import { QueryBuilderContext } from './context';
import {
  applyTarget,
  applyUnit,
  derivePrecision,
  effectivePrecision,
  fieldOffersOffset,
  isAnchoredValue,
  isOffsetShape,
  isReservedAnchor,
  offeredUnits,
  SPECIFIC_DATE_TARGET,
  targetFieldType,
  targetLeadingOptions,
} from './dateRelations';
import HierarchicalFieldSelector from './HierarchicalFieldSelector';
import { getNumericRange, isWithinNumericRange, numericRangeMessage } from './numericRange';
import { DEFAULT_OFFSET_UNIT, EXACT_OFFSET_UNITS, PRECISION_EXACT, TRIGGERED_AT_ANCHOR } from './operators';
import type { EnumOption, FieldType, HierarchicalValueShape, HierarchyHints, NumericRangeHints } from './types';
import { DEFAULT_FIELD_TYPE, FIELD_TYPE, VARIANT_HINT_KEY } from './types';
import { UnknownFieldPathCell, UnknownFieldValueCell } from './UnknownFieldLeaf';

// us_state_select is a typed field — the canonical 50-state list lives in
// server_shared/enums.json (COUNTRY_TO_STATE_MAP.US) and is already deduced to
// the FE, so we render it client-side instead of asking the BE to ship 50
// strings on every condition_fields response.
const US_STATE_OPTIONS: EnumOption[] = Object.entries(COUNTRY_TO_STATE_MAP.US)
  .filter(([code]) => code !== '')
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([value, label]) => ({ value, label }));

const MULTI_VALUE_OPERATORS = new Set(['in', 'notIn', 'anyOf', 'noneOf']);
const UNARY_OPERATORS = new Set(['null', 'notNull', 'empty', 'notEmpty']);

// Threshold above which the hierarchical parent/child pickers switch from
// `MultiSelectField` (scrolling checkbox list) to `MultiSelectAutoComplete`
// (search-as-you-type, still multi-select). 12 keeps the incident-type picker
// (typically 5–10 entries) on the simpler widget; loss_location (251 countries)
// and the US state list (~60) get search.
const HIERARCHICAL_SEARCHABLE_THRESHOLD = 12;

/** The value cell's type dispatch, with no interception in front of it.
 *
 *  Separate from `ValueEditor` because a leaf inside a collection match body needs the dispatch
 *  without the interception: a sub-leaf's field is a bare element name, which the catalog
 *  deliberately does not serve, so `isUnknownFieldPath` answers "unresolvable" for every
 *  correctly-authored one and would leave the body's operands read-only — the cell the shim this
 *  replaced used to render. The nested builder resolves an element against its own
 *  `subproperties` instead, and hands the result here as `fieldData` exactly as the root does.
 *
 *  It is the same dispatch for both, not a stripped-down copy: a `datetime` element reaches the
 *  date editor and its catalog-wide target picker, which is the one place a sub-leaf's operand
 *  is resolved against the catalog rather than the collection. */
export const SubValueEditor: React.FC<ValueEditorProps> = ({ field, fieldData, operator, value, handleOnChange }) => {
  // RQB threads our domain props through `fieldData` (the original Field object).
  const fieldType = (fieldData?.field_type ?? DEFAULT_FIELD_TYPE) as FieldType;
  const enumValues = (fieldData?.enum_values ?? null) as EnumOption[] | null;
  // Narrowed once rather than at each of the three readers below: react-querybuilder v8 types a
  // field's custom props as `unknown` where v6 typed them `any`, and `hints` is an untyped dict
  // on the wire in any case.
  const hints = fieldData?.hints as Record<string, unknown> | undefined;
  const hierarchy = hints?.hierarchy as HierarchyHints | undefined;
  const isVariantField = Boolean(hints?.[VARIANT_HINT_KEY]);
  const numericRange = getNumericRange(hints);

  if (UNARY_OPERATORS.has(operator)) {
    return null;
  }

  const isMultiValue = MULTI_VALUE_OPERATORS.has(operator) || fieldType === FIELD_TYPE.Multiselect;

  if (fieldType === FIELD_TYPE.Hierarchical) {
    return (
      <HierarchicalValueInput
        field={field}
        hierarchy={hierarchy}
        value={value as HierarchicalValueShape | null}
        onChange={handleOnChange}
      />
    );
  }

  if (fieldType === FIELD_TYPE.Boolean) {
    // pt-[8px] aligns the checkbox icon centre with the input-text baseline of the
    // neighbouring TextField cells (label takes ~16px above the input, checkbox has
    // 9px internal padding, so a small top pad lands the icon at the right height).
    const checked = value === true || value === 'true';
    return (
      <span className="gap-1 inline-flex items-center pt-[8px]">
        <Checkbox checked={checked} onChange={(_e, c) => handleOnChange(c)} />
        <span>{checked ? 'true' : 'false'}</span>
      </span>
    );
  }

  if (
    fieldType === FIELD_TYPE.Select ||
    fieldType === FIELD_TYPE.UsStateSelect ||
    fieldType === FIELD_TYPE.YesNoNaUnknown
  ) {
    // us_state_select prefers the field definition's region-aware enum_values
    // (the BE ships the org region's state list — US, AU, …). A missing list
    // (null — e.g. an older payload) falls back to the FE-side US list, but an
    // empty list is honoured: a region with no states must not fall back to US.
    const options = fieldType === FIELD_TYPE.UsStateSelect ? (enumValues ?? US_STATE_OPTIONS) : (enumValues ?? []);
    if (isMultiValue) {
      return (
        <MultiSelectFromEnum
          id={`${field}-value`}
          options={options}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={handleOnChange}
        />
      );
    }
    return (
      <SingleSelectFromEnum
        id={`${field}-value`}
        options={options}
        value={(value as string) ?? ''}
        onChange={handleOnChange}
      />
    );
  }

  if (fieldType === FIELD_TYPE.Multiselect) {
    return (
      <MultiSelectFromEnum
        id={`${field}-value`}
        options={enumValues ?? []}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={handleOnChange}
      />
    );
  }

  if (fieldType === FIELD_TYPE.User) {
    return <UserValueInput field={field} value={value} onChange={handleOnChange} />;
  }

  if (fieldType === FIELD_TYPE.Date || fieldType === FIELD_TYPE.Datetime || fieldType === FIELD_TYPE.Time) {
    return (
      <DateValueInput
        field={field}
        fieldType={fieldType}
        isVariantField={isVariantField}
        value={value}
        onChange={handleOnChange}
      />
    );
  }

  if (fieldType === FIELD_TYPE.Monetary) {
    if (isMultiValue) {
      return (
        <FreeListNumberInput
          id={`${field}-value`}
          value={Array.isArray(value) ? (value as Array<number | string>) : []}
          onChange={handleOnChange}
        />
      );
    }
    return (
      <MonetaryValueTextField
        id={`${field}-value`}
        label="Value"
        value={(value as number | string | undefined) ?? ''}
        onChange={(v) => handleOnChange(v ?? '')}
        fullWidth
      />
    );
  }

  if (fieldType === FIELD_TYPE.Number) {
    if (isMultiValue) {
      return (
        <FreeListNumberInput
          id={`${field}-value`}
          value={Array.isArray(value) ? (value as Array<number | string>) : []}
          range={numericRange}
          onChange={handleOnChange}
        />
      );
    }
    if (numericRange) {
      return (
        <BoundedNumberInput
          id={`${field}-value`}
          value={(value as string | number | undefined) ?? ''}
          range={numericRange}
          onChange={handleOnChange}
        />
      );
    }
    return (
      <TextField<string>
        id={`${field}-value`}
        label="Value"
        type="number"
        value={(value as string) ?? ''}
        onChange={(v) => handleOnChange(v === '' ? '' : Number(v))}
        fullWidth
      />
    );
  }

  // Strings / multiline_string / location / contact
  if (isMultiValue) {
    return (
      <FreeListStringInput
        id={`${field}-value`}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={handleOnChange}
      />
    );
  }
  return (
    <TextField<string>
      id={`${field}-value`}
      label="Value"
      multiline={fieldType === FIELD_TYPE.MultilineString}
      value={(value as string) ?? ''}
      onChange={(v) => handleOnChange(v)}
      fullWidth
    />
  );
};

const ValueEditor: React.FC<ValueEditorProps> = (props) => {
  const { isUnknownFieldPath } = useContext(QueryBuilderContext);
  // Before the dispatch, not inside it: a leaf the registry cannot resolve shows what it stored
  // whatever its operator says, and `null` is a truer answer than an empty cell.
  if (isUnknownFieldPath(props.field)) {
    return <UnknownFieldValueCell value={props.value} />;
  }

  return <SubValueEditor {...props} />;
};

// --- helpers (CMS-Core-backed) ---

const SingleSelectFromEnum: React.FC<{
  id: string;
  options: EnumOption[];
  value: string;
  onChange: (v: string) => void;
}> = ({ id, options, value, onChange }) => (
  <SingleSelectField<string>
    id={id}
    value={value}
    onChange={(v) => onChange(v ?? '')}
    label="Value"
    clearable
    clearSelection={() => onChange('')}
    fullWidth
  >
    {options.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>
        {opt.label}
      </MenuItem>
    ))}
  </SingleSelectField>
);

const MultiSelectFromEnum: React.FC<{
  id: string;
  options: EnumOption[];
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ id, options, value, onChange }) => {
  const optionValues = options.map((o) => o.value);
  const labelByValue = Object.fromEntries(options.map((o) => [o.value, o.label]));
  return (
    <MultiSelectField<string>
      id={id}
      label="Values"
      value={value}
      options={optionValues}
      onChange={onChange}
      renderValue={(selected) => (selected ?? []).map((v) => labelByValue[v] ?? v).join(', ')}
      renderOption={(opt) => labelByValue[opt as string] ?? (opt as string)}
      fullWidth
    />
  );
};

// Raw text is owned locally so trailing `,` and stray spaces survive the
// parse+rejoin cycle. The upstream array is the parsed projection emitted on
// each keystroke. No effect-syncing from `value` — RQB remounts this editor
// (new key) when the rule changes, so the initial value via useState is the
// only sync we need.
const parseStringList = (raw: string) =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const FreeListStringInput: React.FC<{
  id: string;
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ id, value, onChange }) => {
  const [raw, setRaw] = useState(() => value.join(', '));
  return (
    <TextField<string>
      id={id}
      label="Comma-separated values"
      value={raw}
      onChange={(next) => {
        setRaw(next);
        onChange(parseStringList(next));
      }}
      fullWidth
    />
  );
};

// A number field whose provider declared a `range` hint is bounded at the datum, not at the widget:
// `ai.confidence_score` is a fraction, so `>= 54` is not a strict threshold but one that matches
// every agent call that reported a confidence at all. Only the bounded case takes this path — an
// unbounded number field keeps the plain controlled box above.
//
// The box owns its raw text, for the reason `FreeListNumberInput` below does: what the author typed
// stays on screen while the error names the bound, rather than springing back to the last good
// value with no explanation.
const BoundedNumberInput: React.FC<{
  id: string;
  value: string | number;
  range: NumericRangeHints;
  onChange: (v: number | '') => void;
}> = ({ id, value, range, onChange }) => {
  const [raw, setRaw] = useState(() => (value === '' ? '' : String(value)));
  return (
    <TextField<string>
      id={id}
      label="Value"
      type="number"
      value={raw}
      error={!isWithinNumericRange(raw, range)}
      // Stated whenever the field is bounded, not only once it is violated: a fraction is not
      // guessable from the label, and "Confidence Score" reads like a percentage.
      helperText={numericRangeMessage(range)}
      inputProps={{ min: range.min, max: range.max, step: 'any' }}
      onChange={(next) => {
        setRaw(next);
        // Emit `''` rather than the out-of-range number, so the value never reaches the tree — the
        // same contract the comma-separated editor below uses for a token that won't parse. On the
        // notification and assignment dialogs `validateConditionsTree` then trips its "needs a
        // value" guard and blocks Save; the guardrails dialog does not validate its tree, so there
        // the `''` is refused by `coerce_scalar` on save and comes back as a 400.
        onChange(next === '' || !isWithinNumericRange(next, range) ? '' : Number(next));
      }}
      fullWidth
    />
  );
};

const FreeListNumberInput: React.FC<{
  id: string;
  value: Array<number | string>;
  range?: NumericRangeHints;
  onChange: (v: number[]) => void;
}> = ({ id, value, range, onChange }) => {
  const [raw, setRaw] = useState(() => value.join(', '));
  // Surface non-numeric tokens (e.g. "1, abc, 2") inline so the user knows
  // they were dropped — otherwise the upstream onChange silently strips them
  // and "abc" only fails at BE save-time with a less actionable error.
  const tokens = parseStringList(raw);
  const invalidTokens = tokens.filter((t) => Number.isNaN(Number(t)));
  const hasInvalid = invalidTokens.length > 0;
  // Same treatment one step along: a bounded field rejects an out-of-range token for the same
  // reason it rejects a non-numeric one — neither can ever match.
  const outOfRangeTokens = range ? tokens.filter((t) => !isWithinNumericRange(t, range)) : [];
  const hasOutOfRange = outOfRangeTokens.length > 0;
  const helperText = hasInvalid
    ? `Not a number: ${invalidTokens.join(', ')}`
    : hasOutOfRange
      ? `${numericRangeMessage(range!)} Out of range: ${outOfRangeTokens.join(', ')}`
      : range
        ? numericRangeMessage(range)
        : undefined;
  return (
    <TextField<string>
      id={id}
      label="Comma-separated numbers"
      value={raw}
      error={hasInvalid || hasOutOfRange}
      helperText={helperText}
      onChange={(next) => {
        setRaw(next);
        const parsed = parseStringList(next);
        const anyInvalid = parsed.some(
          (t) => Number.isNaN(Number(t)) || (range !== undefined && !isWithinNumericRange(t, range))
        );
        // Emit `[]` when any token won't parse: the form-level
        // validateConditionsTree then trips its "needs at least one value"
        // guard and blocks Save. Without this, we'd silently drop bad tokens
        // and a `[1, abc, 2]` input would save as `[1, 2]`.
        onChange(anyInvalid ? [] : parsed.map(Number));
      }}
      fullWidth
    />
  );
};

// Grid inside the single Value cell of a date leaf: the target picker, plus the date box
// beside it for a specific date, or the count and unit in front of it for an offset.
//
// The cell is narrow and its width is not ours to choose: every rule dialog is
// `maxWidth="md"`, so the paper is 960px whatever the viewport, which leaves a 908px
// conditions card and a value track of 6/12 of the row's free space — 394px, dropping
// further once a narrow window pulls the paper under 960. Fixed tracks do not shrink
// below their minimum, they paint outside the cell and over the row's delete icon, so
// the row sizes by `auto-fit` and wraps instead.
//
// The floor is 110px rather than 140px because an offset holds three tracks, and three fit
// on one line only under it: 3×110 + 2×16 = 362 ≤ 394, where 3×140 + 2×16 = 452 would have
// wrapped to 2×2 at every viewport from 1024 up. `auto-fit` keeps the property that matters
// either way — it drops columns until they fit, so the row never paints outside the cell —
// and a lower floor only ever wraps later.
const VALUE_GRID = 'grid-cols-[repeat(auto-fit,minmax(110px,1fr))]';

/** Said under the target on an exact comparison: the two instants are compared with their time
 *  of day, not by calendar day. */
const EXACT_HELPER_TEXT = 'to the minute';

/**
 * What the comparison is measured against: a specific date, When the event occurred, When this
 * rule runs, or a catalog date field — or a path the catalog no longer serves.
 *
 * That last one is the same drift the leaf's own field can suffer, and it locked an admin out
 * the same way: the picker showed the raw path over a tree the path is not in, and
 * `validateConditionsTree` blocked every save until the target was replaced — which changes
 * what the condition means. It states the stored path instead and carries the explanation,
 * exactly as an unresolvable Field cell does. A target that is invalid for a different reason
 * (the event time on a rule with no event, say) is shown as it is and reported by the
 * validator — never swapped for one the author did not choose.
 */
const TargetCell: React.FC<{
  field: string;
  /** `SPECIFIC_DATE_TARGET`, a reserved anchor, a catalog path, or `''` while unpicked. */
  target: string;
  leadingOptions: FieldOption[];
  options: FieldOption[];
  helperText?: string;
  onChange: (target: string) => void;
}> = ({ field, target, leadingOptions, options, helperText, onChange }) => {
  const { isUnknownFieldPath } = useContext(QueryBuilderContext);

  // The sentinel and the reserved anchors are not paths, so they are never "unknown" ones.
  if (target !== SPECIFIC_DATE_TARGET && !isReservedAnchor(target) && isUnknownFieldPath(target)) {
    // No chip: the leaf's own Field cell already carries one for this row.
    return <UnknownFieldPathCell path={target} label="Compared to" showStatus={false} />;
  }
  return (
    <HierarchicalFieldSelector
      id={`${field}-anchor`}
      label="Compared to"
      value={target}
      options={options}
      leadingOptions={leadingOptions}
      helperText={helperText}
      handleOnChange={onChange}
      triggerTestId="qb-anchor-trigger"
      searchTestId="qb-anchor-search"
    />
  );
};

const DateValueInput: React.FC<{
  field: string;
  fieldType: FieldType;
  isVariantField: boolean;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ field, fieldType, isVariantField, value, onChange }) => {
  const { dateFields, hasTriggeringEvent } = useContext(QueryBuilderContext);

  // `group` is carried through because `buildFieldTree` nests on it and drops an entry
  // without one at the root. This is the same component as the field picker one cell to
  // the left, so omitting it made a date field sit in one place there and a different
  // place here: browsing for `Effective Date` means opening `Policy` in one control and
  // scanning an undifferentiated list in the other. For a control whose whole job is
  // comparing one date against another, matching the picker beside it is the point.
  const targetOptions = useMemo(
    () => dateFields.map((dateField) => ({ name: dateField.path, label: dateField.label, group: dateField.group })),
    [dateFields]
  );
  const dateFieldsByPath = useMemo(
    () => Object.fromEntries(dateFields.map((dateField) => [dateField.path, dateField])),
    [dateFields]
  );

  // A field that cannot carry an anchored value is the date box on its own.
  if (!fieldOffersOffset(fieldType, isVariantField)) {
    return <AbsoluteDateInput field={field} fieldType={fieldType} value={value} onChange={onChange} />;
  }

  const offset = isOffsetShape(value) ? value : null;
  const anchor = isAnchoredValue(value) ? value.anchor : undefined;
  // A plain date string — including the empty one a fresh row holds — is the specific-date target.
  const target = anchor === undefined ? SPECIFIC_DATE_TARGET : typeof anchor === 'string' ? anchor : '';
  // What a target implies once picked: the marker a pick stamps, and whether Hours is on offer.
  const precisionFor = (candidate: string) => derivePrecision(fieldType, targetFieldType(candidate, dateFieldsByPath));

  const targetCell = (
    <TargetCell
      field={field}
      target={target}
      leadingOptions={targetLeadingOptions({
        offersSpecificDate: !offset,
        // Also while it is the stored target, so a leaf carrying it names it rather than a raw
        // token — the validator is what says it cannot be saved on this rule.
        offersEventTime: hasTriggeringEvent || target === TRIGGERED_AT_ANCHOR,
      })}
      options={targetOptions}
      helperText={effectivePrecision(value) === PRECISION_EXACT ? EXACT_HELPER_TEXT : undefined}
      onChange={(nextTarget) => onChange(applyTarget(value, nextTarget, precisionFor(nextTarget)))}
    />
  );

  if (!offset) {
    return (
      <div className={`grid gap-16 ${VALUE_GRID}`}>
        {targetCell}
        {target === SPECIFIC_DATE_TARGET && (
          <AbsoluteDateInput field={field} fieldType={fieldType} value={value} onChange={onChange} />
        )}
      </div>
    );
  }

  // A stored unit this precision does not offer — Hours on a whole-day comparison, or a token an
  // import wrote — is listed anyway, so the cell states it rather than rendering blank; the
  // validator reports it.
  const units = offeredUnits(precisionFor(target));
  const storedUnit = String(offset.unit ?? '');
  const unitOptions =
    !storedUnit || units.some((unit) => unit.name === storedUnit)
      ? units
      : [
          ...units,
          { name: storedUnit, label: EXACT_OFFSET_UNITS.find((u) => u.name === storedUnit)?.label ?? storedUnit },
        ];

  // Direction is absent by design: it is one third of what the relation phrase names, so
  // it is chosen alongside the comparison rather than three cells away from it, where the
  // two controls that invert each other sat as far apart as the row allowed.
  return (
    <div className={`grid gap-16 ${VALUE_GRID}`}>
      <TextField<string>
        id={`${field}-count`}
        label="How many"
        type="number"
        value={offset.n === '' ? '' : String(offset.n)}
        // An emptied box stays empty rather than coercing to 0 — otherwise clearing
        // it would silently save an offset of zero.
        onChange={(v) => onChange({ ...offset, n: v === '' ? '' : Number(v) })}
        fullWidth
      />
      <SingleSelectField<string>
        id={`${field}-unit`}
        label="Unit"
        value={offset.unit}
        onChange={(v) => onChange(applyUnit(offset, v ?? DEFAULT_OFFSET_UNIT))}
        fullWidth
      >
        {unitOptions.map((unit) => (
          <MenuItem key={unit.name} value={unit.name}>
            {unit.label}
          </MenuItem>
        ))}
      </SingleSelectField>
      {targetCell}
    </div>
  );
};

/** A user leaf's operand is another user field — there is no literal user id an author could type —
 *  so the value cell is the "Compared to" picker alone, writing `{anchor: <path>}`. The leaf's own
 *  field is left out: comparing a user with itself is always true. */
const UserValueInput: React.FC<{
  field: string;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ field, value, onChange }) => {
  const { userFields } = useContext(QueryBuilderContext);
  const options = useMemo(
    () =>
      userFields
        .filter((userField) => userField.path !== field)
        .map((userField) => ({ name: userField.path, label: userField.label, group: userField.group })),
    [userFields, field]
  );
  const target = isAnchoredValue(value) && typeof value.anchor === 'string' ? value.anchor : '';
  return (
    <TargetCell
      field={field}
      target={target}
      leadingOptions={[]}
      options={options}
      onChange={(next) => onChange({ anchor: next })}
    />
  );
};

const AbsoluteDateInput: React.FC<{
  field: string;
  fieldType: FieldType;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ field, fieldType, value, onChange }) => {
  // Pass null (not '') when no value yet — MUI DateTimePicker treats empty
  // string as an invalid date and falls back to "now", showing "Invalid Date
  // Format" + the current time as a default.
  const dateValue = typeof value === 'string' && value ? value : null;
  return (
    <DatePickerField
      id={`${field}-value`}
      label="Value"
      value={dateValue}
      isDateTimeSelection={fieldType === FIELD_TYPE.Datetime}
      isOnlyTimeSelection={fieldType === FIELD_TYPE.Time}
      fullWidth
      onChange={(picked) => {
        // DatePickerField hands us a moment-ish object (or null). Emit ISO string for CEL.
        if (!picked) {
          onChange('');
          return;
        }
        const m = moment.isMoment(picked) ? picked : moment(picked);
        // A time is written to the minute, the spelling the BE normalizes a time literal to, so
        // the stored leaf matches the compiled one.
        const iso =
          fieldType === FIELD_TYPE.Date
            ? m.format(DateFormat.ISO)
            : fieldType === FIELD_TYPE.Time
              ? m.format(DateFormat.TIME_24H)
              : m.toISOString();
        onChange(iso);
      }}
    />
  );
};

const HierarchicalValueInput: React.FC<{
  field: string;
  hierarchy: HierarchyHints | undefined;
  value: HierarchicalValueShape | null;
  onChange: (v: HierarchicalValueShape) => void;
}> = ({ field, hierarchy, value, onChange }) => {
  const parents = hierarchy?.values ?? [];
  // `parent` may be string (single) or string[] (multi). Normalise to a list
  // for the multi-select widget; emit single string when exactly one is
  // selected so the BE compiler hits its single-parent branch.
  const selectedParents = Array.isArray(value?.parent)
    ? (value!.parent as string[])
    : value?.parent
      ? [value.parent as string]
      : [];
  const selectedChildren = value?.children ?? [];
  // Children are valid only on single-parent leaves — different parents may
  // have different valid children, so the multi-parent shape forbids them.
  const childSourceParent = selectedParents.length === 1 ? parents.find((p) => p.value === selectedParents[0]) : null;
  const childOptions = childSourceParent?.children ?? [];
  // Whether this hierarchy has a second level at all — distinct from a parent with
  // no enumerated catalog (country=GB below), which still accepts free text. The
  // legacy GL incident config is the former: no incident type defines sub-types and
  // `GLClaim.incident_subtype` is always null, so a sub-type condition could never
  // match. Drop the slot rather than offer a free-text box that compiles to an
  // unsatisfiable clause.
  //
  // Taken from the BE hint rather than inferred from `parents`: the catalog is
  // intersected across the scope's LOBs, so a mixed-LOB rule whose sub-type sets
  // don't overlap arrives with empty `children` on every parent while
  // `incident_sub_type` still holds values. Inferring would drop that author's child
  // control. Defaults to true so a hierarchy that doesn't declare the flag (the
  // country/state composite) keeps both levels.
  const hasChildDimension = hierarchy?.has_child_dimension ?? true;
  // Per-parent child-label override: lets the picker render parent-specific
  // labels (e.g. "State" for US, "UK Country" for GB) instead of the generic
  // `child_label` fallback. When no parent is selected, or no override
  // matches, fall through to the default placeholder.
  const defaultChildLabel = hierarchy?.child_label ?? 'Children';
  const effectiveChildLabel =
    selectedParents.length === 1
      ? (hierarchy?.child_label_overrides?.[selectedParents[0]] ?? defaultChildLabel)
      : defaultChildLabel;
  // Free-text fallback: a known parent with an empty children list signals
  // "no enumerated child catalog" (e.g. country=GB in country→state). Mirrors
  // `StateFieldFormik` in `GlobalLossLocation.jsx`: enumerate when we have a
  // catalog, accept free text otherwise. Wire contract preserved — emit as a
  // 1-element `string[]` so the BE compiler's existing array branch handles it.
  const renderChildAsFreeText =
    childSourceParent !== null && childSourceParent !== undefined && childOptions.length === 0;
  const childLabelByValue = Object.fromEntries(childOptions.map((c) => [c.value, c.label]));
  const parentLabelByValue = Object.fromEntries(parents.map((p) => [p.value, p.label]));

  // Hiding the child half doesn't clear it. A rule saved as
  // `{parent: 'X', children: ['Y']}` whose hierarchy later loses its child dimension
  // renders with no child control, so the author can neither see nor remove the
  // clause — but the BE compiler still emits `child_data_path in [...]` for a
  // non-empty `children` on every re-save. Drop it, so what the author sees is what
  // compiles. This marks the form dirty without the author touching anything, which is
  // intended: the correction only reaches the BE if they save, and leaving it unsaved
  // would keep the invisible clause compiling.
  const hasStaleChildren = !hasChildDimension && selectedChildren.length > 0;
  useEffect(() => {
    if (hasStaleChildren && value) {
      onChange({ ...value, children: [] });
    }
  }, [hasStaleChildren, onChange, value]);

  const handleParentChange = (next: string[]) => {
    if (next.length === 0) {
      onChange({ parent: '', children: [] });
    } else if (next.length === 1) {
      // Preserve children only when the single parent didn't change — switching
      // parents invalidates the child list.
      const sameParent = selectedParents.length === 1 && selectedParents[0] === next[0];
      onChange({ parent: next[0], children: sameParent ? selectedChildren : [] });
    } else {
      onChange({ parent: next, children: [] });
    }
  };

  const parentOptions = parents.map((p) => p.value);
  const useAutocompleteForParent = parentOptions.length > HIERARCHICAL_SEARCHABLE_THRESHOLD;
  const useAutocompleteForChild = childOptions.length > HIERARCHICAL_SEARCHABLE_THRESHOLD;
  // Child rendering modes (ordered by selectedParents.length):
  //   - 1 parent  → active child (mode determined by childOptions: free-text
  //                 for empty catalogs, autocomplete above threshold, else
  //                 plain multi-select).
  //   - 0 parents → disabled placeholder of the same widget type the parent
  //                 uses, so authors see "there's a second step here" instead
  //                 of an empty column. Matching widget types keeps baselines
  //                 aligned across the row.
  //   - 2+ parents → child hidden entirely. The hierarchical compiler forbids
  //                 children on multi-parent leaves (different parents may have
  //                 incompatible child sets), so a disabled "fill the prereq"
  //                 affordance would be misleading here.
  // All three are moot without a child dimension — that hierarchy is one level deep.
  const isActiveChild = hasChildDimension && selectedParents.length === 1;
  const isPlaceholderChild = hasChildDimension && selectedParents.length === 0;
  const showChildSlot = isActiveChild || isPlaceholderChild;

  return (
    <div className={`grid gap-16 ${showChildSlot ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {useAutocompleteForParent ? (
        <MultiSelectAutoComplete<string>
          id={`${field}-parent`}
          label={hierarchy?.parent_label ?? 'Parent'}
          value={selectedParents}
          options={parentOptions}
          getOptionLabel={(opt) => parentLabelByValue[opt] ?? opt}
          onChange={(next) => handleParentChange(next ?? [])}
        />
      ) : (
        <MultiSelectField<string>
          id={`${field}-parent`}
          label={hierarchy?.parent_label ?? 'Parent'}
          value={selectedParents}
          options={parentOptions}
          onChange={handleParentChange}
          renderValue={(selected) => (selected ?? []).map((v) => parentLabelByValue[v] ?? v).join(', ')}
          renderOption={(opt) => parentLabelByValue[opt as string] ?? (opt as string)}
          fullWidth
        />
      )}
      {isPlaceholderChild &&
        (useAutocompleteForParent ? (
          <MultiSelectAutoComplete<string>
            id={`${field}-children`}
            label={effectiveChildLabel}
            value={[]}
            options={[]}
            getOptionLabel={(opt) => opt}
            onChange={() => undefined}
            disabled
          />
        ) : (
          <MultiSelectField<string>
            id={`${field}-children`}
            label={effectiveChildLabel}
            value={[]}
            options={[]}
            onChange={() => undefined}
            renderValue={() => ''}
            disabled
            fullWidth
          />
        ))}
      {isActiveChild &&
        (renderChildAsFreeText ? (
          <TextField<string>
            id={`${field}-children`}
            label={effectiveChildLabel}
            value={selectedChildren[0] ?? ''}
            onChange={(newValue) => {
              const text = (newValue ?? '').trim();
              onChange({ parent: selectedParents[0], children: text ? [text] : [] });
            }}
            fullWidth
          />
        ) : useAutocompleteForChild ? (
          <MultiSelectAutoComplete<string>
            id={`${field}-children`}
            label={effectiveChildLabel}
            value={selectedChildren}
            options={childOptions.map((c) => c.value)}
            getOptionLabel={(opt) => childLabelByValue[opt] ?? opt}
            onChange={(next) => onChange({ parent: selectedParents[0], children: next ?? [] })}
          />
        ) : (
          <MultiSelectField<string>
            id={`${field}-children`}
            label={effectiveChildLabel}
            value={selectedChildren}
            options={childOptions.map((c) => c.value)}
            onChange={(next) => onChange({ parent: selectedParents[0], children: next })}
            renderValue={(selected) => (selected ?? []).map((v) => childLabelByValue[v] ?? v).join(', ')}
            renderOption={(opt) => childLabelByValue[opt as string] ?? (opt as string)}
            fullWidth
          />
        ))}
    </div>
  );
};

export default ValueEditor;
